/**
 * The runner at `/practice/today` (pedagogy spec §6.3.4, D-3): the day's steps as a timeline, the
 * step the student is on with its start button, "Hoppa över det här steget" and "Sluta här för
 * idag", and "Anpassa". Each step hands off to the page that runs it and comes back with
 * `?done=<step>`, which this page reads once, records and strips. A refresh is safe: the timeline
 * lives in `sessionStorage` (`session.ts`), and the plan is re-read from the server, so a step
 * that was left half done offers only what is still to do.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useTopicsAndChapters } from "@client/api/topics";
import { useLocalizedText } from "@client/components/Localized";
import { Button, Card, PageHeader, Spinner, useToast } from "@client/components/ui";
import { useT } from "@client/i18n";
import { messageForError } from "@client/lib/errors";
import { saveSession } from "../session";
import { AdjustDialog } from "./AdjustDialog";
import { useTodayData } from "./api";
import { PRIMARY_LINK } from "./linkClass";
import {
  clearRunner,
  doneStepOf,
  finishStep,
  loadRunner,
  newRunner,
  reopenStep,
  saveRunner,
  skipStep,
  startStep,
  stopForToday,
  type TodayRunnerState,
} from "./session";
import { newTopicSession, resumableSession, retestSession, cardsHref } from "./start";
import { StepList } from "./StepList";
import { buildSteps, hasBegun, isFinished, type TodayStep } from "./steps";
import { TodayTally } from "./TodayEvidence";
import { TodayExtras } from "./TodayExtras";

export function TodayPage() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const topics = useTopicsAndChapters();
  const localize = useLocalizedText();
  const { settings, skip, plan, mixed, apply } = useTodayData({ questions: true });
  const [stored, setStored] = useState<TodayRunnerState | null>(() => loadRunner());
  const [adjusting, setAdjusting] = useState(false);

  const data = plan.data;
  const dayKey = data?.dayKey;
  // The state belongs to one day: another day's is dropped as soon as the plan names today.
  const runner = stored && dayKey !== undefined && stored.dayKey === dayKey ? stored : null;
  useEffect(() => {
    if (stored && dayKey !== undefined && stored.dayKey !== dayKey) {
      clearRunner();
      setStored(null);
    }
  }, [stored, dayKey]);

  function update(change: (state: TodayRunnerState) => TodayRunnerState) {
    if (dayKey === undefined) return;
    const next = change(runner ?? newRunner(dayKey));
    saveRunner(next);
    setStored(next);
  }

  // `?done=<step>` is the hand-off coming back: record it, then take it off the address.
  const doneStep = doneStepOf(params);
  useEffect(() => {
    if (doneStep === null || dayKey === undefined) return;
    update((state) => finishStep(state, doneStep));
    setParams({}, { replace: true });
    // `update` closes over the state this effect should not re-run for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doneStep, dayKey]);

  const steps = useMemo(
    () => (data ? buildSteps({ plan: data, mixed: mixed.data, settings, runner }) : []),
    [data, mixed.data, settings, runner],
  );
  const current = steps.find((step) => step.current);
  const finished = isFinished(steps);

  const topicLabel = (id: string) => {
    const topic = topics.data?.topics.find((candidate) => candidate.id === id);
    return topic ? localize(topic.name) : id;
  };

  /** Starts the practice step: back into its own session, else a new one from the plan. */
  function beginPractice(step: TodayStep) {
    if (!data || (step.id !== "retest" && step.id !== "new")) return;
    const session =
      resumableSession(step.id, data.dayKey) ??
      (step.id === "retest" ? retestSession(data, settings.retests) : newTopicSession(data));
    if (!session) {
      toast.error(t("common.nothingHere"));
      return;
    }
    update((state) =>
      startStep(state, step.id, {
        count: step.count,
        ...(step.topicId ? { topicId: step.topicId } : {}),
      }),
    );
    saveSession(session);
    navigate("/practice/session");
  }

  function actionLabel(step: TodayStep): string {
    if (step.status === "started") return t("practice.today.continue");
    return hasBegun(steps) ? t("practice.today.nextStep") : t("practice.today.start");
  }

  if (plan.isError) {
    return (
      <div className="mx-auto max-w-2xl">
        <PageHeader title={t("practice.today.title")} />
        <p role="alert" className="text-sm text-red-700 dark:text-red-300">
          {messageForError(plan.error, t)}
        </p>
        <Button className="mt-4" variant="secondary" onClick={() => void plan.refetch()}>
          {t("common.retry")}
        </Button>
      </div>
    );
  }
  if (!data || mixed.isLoading) {
    return (
      <div className="mx-auto max-w-2xl">
        <PageHeader title={t("practice.today.title")} />
        <Spinner size="md" label={t("common.loading")} />
      </div>
    );
  }
  if (data.examActive) {
    return (
      <div className="mx-auto max-w-2xl">
        <PageHeader title={t("practice.today.title")} />
        <p
          role="status"
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-accent-900 dark:border-accent-700 dark:bg-accent-950 dark:text-accent-100"
        >
          <span>
            <strong className="font-medium">{t("home.activeExam.title")}</strong>{" "}
            {t("home.activeExam.body")}
          </span>
          <Link
            to="/exam/session"
            className="font-medium underline underline-offset-2 hover:no-underline"
          >
            {t("home.activeExam.link")}
          </Link>
        </p>
      </div>
    );
  }

  const busy = plan.isPlaceholderData;
  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        title={t("practice.today.title")}
        description={t("practice.today.description")}
        actions={
          <Button variant="secondary" onClick={() => setAdjusting(true)}>
            {t("practice.today.adjust")}
          </Button>
        }
      />
      <Card>
        {steps.length === 0 ? (
          // The exam's suggestions are something planned too: no "nothing today" beside them.
          data.extras.length === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-ink-700 dark:text-ink-200">{t("practice.today.empty")}</p>
              <p className="flex flex-wrap gap-4 text-sm font-medium">
                <Link to="/practice" className="underline underline-offset-2 hover:no-underline">
                  {t("practice.nav")}
                </Link>
                <Link to="/flashcards" className="underline underline-offset-2 hover:no-underline">
                  {t("flashcards.nav")}
                </Link>
                <Link to="/exam" className="underline underline-offset-2 hover:no-underline">
                  {t("exam.nav")}
                </Link>
              </p>
            </div>
          )
        ) : (
          <>
            <StepList
              steps={steps}
              topicLabel={topicLabel}
              action={(step) =>
                step.status === "skipped" ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="max-sm:min-h-11"
                    onClick={() => update((state) => reopenStep(state, step.id))}
                  >
                    {t("practice.today.doAnyway")}
                  </Button>
                ) : null
              }
            />
            {finished ? (
              <div className="mt-4 space-y-3">
                <p role="status" className="font-medium text-ink-900 dark:text-ink-50">
                  {t("practice.today.finished")}
                </p>
                {data.evidence && <TodayTally evidence={data.evidence} />}
                <p className="flex flex-wrap gap-4 text-sm font-medium">
                  <Link to="/practice" className="underline underline-offset-2 hover:no-underline">
                    {t("practice.summary.newSession")}
                  </Link>
                  <Link to="/progress" className="underline underline-offset-2 hover:no-underline">
                    {t("practice.today.evidence.open")}
                  </Link>
                </p>
              </div>
            ) : (
              current && (
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  {current.id === "cards" ? (
                    <Link
                      to={cardsHref(current.count)}
                      className={PRIMARY_LINK}
                      onClick={() =>
                        update((state) => startStep(state, "cards", { count: current.count }))
                      }
                    >
                      {actionLabel(current)}
                    </Link>
                  ) : (
                    <Button disabled={busy} onClick={() => beginPractice(current)}>
                      {actionLabel(current)}
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    onClick={() => update((state) => skipStep(state, current.id))}
                  >
                    {t("practice.today.skipStep")}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() =>
                      update((state) =>
                        stopForToday(
                          state,
                          steps.map((step) => step.id),
                        ),
                      )
                    }
                  >
                    {t("practice.today.stopHere")}
                  </Button>
                </div>
              )
            )}
          </>
        )}
        <TodayExtras extras={data.extras} headingLevel={2} />
      </Card>
      <AdjustDialog
        open={adjusting}
        onClose={() => setAdjusting(false)}
        settings={settings}
        skip={skip}
        plannedTopicId={data.newTopic?.topicId ?? null}
        onSave={apply}
      />
    </div>
  );
}

export default TodayPage;
